import { PushNotification, SMSNotification } from '@the-real-insight/tri-model';

import { ALERT_TEXT_NAMESPACE } from './alertPlaceholders';
import { Translate } from './AlertActionEditor';
import { viewOfConditions } from './alertConditionModel';
import { cooldownToFields, parseCooldown, CooldownUnit } from './alertCooldown';
import { noUplinkDays } from './noUplink';
import { deviceAlarmsOf } from './alertDeviceAlarms';
import { roleLabelKey } from './pushRecipients';
import { summarizeCondition, summarizeConditions, SummaryWords } from './alertRuleSummary';

/** Marks a gap in the sentence ("still to fill in"); the renderer shows it muted instead of as text. */
export const GAP_OPEN = '\u0001';
export const GAP_CLOSE = '\u0002';

const T = ALERT_TEXT_NAMESPACE;

const gap = (text: string): string => `${GAP_OPEN}${text}${GAP_CLOSE}`;

export const operatorWords = (t: Translate): Record<string, string> => ({
    equal: t(`${T}operatorEqual`),
    less: t(`${T}operatorLess`),
    lessEqual: t(`${T}operatorLessEqual`),
    greater: t(`${T}operatorGreater`),
    greaterEqual: t(`${T}operatorGreaterEqual`),
});

/**
 * What the alert does, in one or two plain sentences: "When pressure below 10 bar, an e-mail to
 * x@y.de is sent. Applies to: all devices. At most one alert per device every 24 hours."
 *
 * Missing pieces are `GAP_OPEN … GAP_CLOSE` spans — a gentle gap, not an error.
 */
export const buildSentence = (input: {
    t: Translate;
    language: string;
    conditions: any[];
    actions: any[];
    scope: string;
    cooldownAmount: string;
    cooldownUnit: CooldownUnit;
}): string => {
    const { t, language } = input;
    const words: SummaryWords = { and: t(`${T}sentence.and`), or: t(`${T}summaryOr`), operators: operatorWords(t) };
    const days = noUplinkDays(input.conditions);

    let condition: string;

    if (days !== null) {
        condition = days === undefined
            ? gap(t(`${T}sentence.gapCondition`))
            : t(`${T}sentence.noUplink`, { days: String(days) });
    } else {
        const view = viewOfConditions(input.conditions);

        if (view.kind === 'advanced' || deviceAlarmsOf(input.conditions)) {
            condition = summarizeConditions(input.conditions, language, words);
        } else {
            const parts = view.rows
                .map((row) => summarizeCondition(row, language, words))
                .filter((part) => part !== '');

            condition = parts.length > 0
                ? parts.join(` ${view.match === 'any' ? words.or : words.and} `)
                : gap(t(`${T}sentence.gapCondition`));
        }
    }

    const sendings = input.actions
        .map((action) => {
            if (action instanceof PushNotification || action?.actionType === 'PushNotification') {
                const roles = (action.recipientRoles ?? []).map((role: string) => t(roleLabelKey(role)));
                const people = (action.recipientUsers ?? []).length;
                const names = [...roles, ...(people > 0 ? [t(`${T}sentence.pushPeople`, { count: people })] : [])];

                return t(`${T}sentence.pushTo`, {
                    to: names.length === 0 ? gap(t(`${T}sentence.gapRecipient`)) : names.join(', '),
                });
            }

            const isEmail = !(action instanceof SMSNotification || action?.actionType === 'SMSNotification');
            const target = String((isEmail ? action?.to : action?.phoneNumber) ?? '').trim();

            return t(`${T}sentence.${isEmail ? 'mailTo' : 'smsTo'}`, {
                to: target === '' ? gap(t(`${T}sentence.gapRecipient`)) : target,
            });
        });
    const notify = sendings.length === 0
        ? gap(t(`${T}sentence.gapNotify`))
        : t(`${T}sentence.send`, { what: sendings.join(` ${words.and} `) });

    const parts = [t(`${T}sentence.main`, { condition, notify }), t(`${T}sentence.scope`, { scope: input.scope })];

    if (days === null) {
        const cooldown = parseCooldown(input.cooldownAmount, input.cooldownUnit);
        const fields = cooldown.minutes === null
            ? { amount: '24', unit: 'hours' as CooldownUnit }
            : cooldownToFields(cooldown.minutes);

        if (!cooldown.invalid) {
            const one = fields.amount === '1';
            const unit = t(`${T}unitWord.${fields.unit}${one ? 'One' : ''}`);

            parts.push(t(`${T}sentence.wait`, { value: `${fields.amount} ${unit}` }));
        }
    }

    return parts.join(' ');
};
